import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import HomeIcon from '@mui/icons-material/Home';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const solutions = [
  {
    icon: <HomeIcon sx={{ fontSize: 40 }} />,
    title: "First-Time Homebuyers",
    description: "Specialized programs and guidance for first-time homebuyers"
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    title: "Refinancing",
    description: "Lower your monthly payments with competitive rates"
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    title: "Investment Properties",
    description: "Grow your portfolio with investment property financing"
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: "Secure Mortgages",
    description: "Fixed and variable rate options for your peace of mind"
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: "Quick Processing",
    description: "Fast approval process with minimal documentation"
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
    title: "Expert Support",
    description: "Dedicated mortgage specialists at your service"
  }
];

const viewportConfig = { 
  threshold: 0.1,
  triggerOnce: false,
  rootMargin: "-50px 0px"
};

const containerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

const SolutionsGrid = () => {
  const [ref, inView] = useInView(viewportConfig);
  const controls = useAnimation();

  React.useEffect(() => {
    controls.start(inView ? "visible" : "hidden");
  }, [controls, inView]);

  return (
    <Box
      ref={ref}
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      sx={{ 
        width: '100%',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ 
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        <Typography 
          component={motion.div}
          variants={itemVariants}
          variant="h4" 
          sx={{ 
            textAlign: 'left', 
            color: '#64B5F6',
            fontWeight: 'bold',
            mb: 6,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              bottom: -10,
              width: '80px',
              height: '4px',
              background: 'linear-gradient(to right, #64B5F6, transparent)',
              borderRadius: '2px'
            }
          }}
        >
          We Provide Solutions
        </Typography>

        <Grid 
          container 
          spacing={{ xs: 3, sm: 4, md: 4 }}
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: { xs: 3, sm: 4, md: 4 },
            width: '100%',
            mx: 'auto'
          }}
        >
          {solutions.map((solution, index) => (
            <Grid 
              item 
              key={index}
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Box
                component={motion.div}
                variants={itemVariants}
                whileHover={{ 
                  y: -3,
                  transition: { duration: 0.2 }
                }}
                sx={{
                  height: '320px',
                  width: '100%',
                  maxWidth: '360px',
                  p: 3,
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ 
                    color: '#64B5F6',
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(100, 181, 246, 0.1)'
                  }}>
                    {solution.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ 
                      color: '#1A1A1A',
                      mb: 2,
                      fontWeight: 500
                    }}>
                      {solution.title}
                    </Typography>
                    <Typography variant="body1" sx={{ 
                      color: '#666',
                      lineHeight: 1.6
                    }}>
                      {solution.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default SolutionsGrid; 